import { type DropDownMenuItemProps, DropdownMenu } from '@/Components/filterbar/components/DropdownMenu';
import { useRelayStore } from '@/graphql/useRelayStore';
import { atom, useAtom } from 'jotai';
import React, { useEffect, useMemo } from 'react';
import { z } from 'zod';
import { FilterBadge } from '../components/FilterBadge';
import { __useFilterState } from '../useFilterState';

export const hubGroupSearchSchema = z.object({
  hubGroup: z.array(z.string()).nullish(),
});

export const hubGroupComponentProps = z.object({
  displayName: z.string().optional(),
});

type HubGroupFilterProps = z.infer<typeof hubGroupComponentProps>;

const groupsAtom = atom<string[]>([]);

const HubGroupFilter = ({ displayName = 'Group' }: HubGroupFilterProps) => {
  const { slowFilters, fastFilters, updateFilter, isUpdating } = __useFilterState(hubGroupSearchSchema);

  // Get the groups from the relay store
  // @todo: maybe we should use a query instead of the store
  // this way we can search for groups and get a bigger list
  const [groups, setGroups] = useAtom(groupsAtom);
  const data = useRelayStore((state) => state.Hubs_Pagination_Fragment.paginationData?.data);
  useEffect(() => {
    if (!data?.hubs?.edges?.length) return;

    setGroups((prev) => {
      const newGroups =
        data.hubs?.edges?.reduce<string[]>((acc, edge) => {
          const group = edge?.node?.group;
          if (!!group && typeof group === 'string' && group.length > 0) {
            if (!acc.includes(group) && !prev.includes(group)) {
              acc.push(group);
            }
          }
          return acc;
        }, []) || [];

      return [...prev, ...newGroups];
    });
  }, [data?.hubs?.edges, setGroups]);

  const menuItems: DropDownMenuItemProps[] = useMemo(() => {
    return groups.map((group) => ({
      name: group,
      value: group,
      onClick: () => {
        updateFilter((prev) => {
          let newGroups = [...(prev?.hubGroup || []), group];
          if (prev?.hubGroup?.includes(group)) {
            newGroups = prev.hubGroup.filter((g) => g !== group);
          }

          return {
            hubGroup: newGroups.length ? newGroups : undefined,
          };
        });
      },
    })) as DropDownMenuItemProps[];
  }, [groups, updateFilter]);

  useEffect(() => {
    const selectedGroups = fastFilters?.hubGroup || [];
    if (!selectedGroups.length) return;

    const validGroups = selectedGroups.filter((group) => groups.includes(group));

    if (validGroups.length !== selectedGroups.length) {
      updateFilter({
        hubGroup: validGroups.length ? validGroups : undefined,
      });
    }
  }, [fastFilters, groups, updateFilter]);

  return (
    <div style={{ position: 'relative' }}>
      <DropdownMenu
        className="z-btn-6"
        name={displayName || 'Group'}
        menuList={menuItems}
        type="multiple-select"
        options={{
          isSearchable: true,
          showCheckedOnTop: true,
          clearSearchOnClose: true,

          filterBar: slowFilters,
          filterBarKey: 'hubGroup',
        }}
      />
      <FilterBadge count={fastFilters?.hubGroup?.length || 0} isUpdating={isUpdating} />
    </div>
  );
};

const HubGroupFilterMemo = React.memo(HubGroupFilter);

export const FilterConfig_HubGroup = {
  hubGroup: {
    searchSchema: hubGroupSearchSchema,
    component: HubGroupFilterMemo,
    componentProps: hubGroupComponentProps,
  },
} as const;
